import api from './api';
import { Product, PaginatedResponse } from '../types';

/**
 * Product service for API calls
 */
export const productService = {
  /**
   * Get all products (unpaginated - loads every page sequentially)
   * @returns List of all products
   */
  async getAll(): Promise<Product[]> {
    try {
      const allProducts: Product[] = [];
      let page = 1;
      let lastPage = 1;

      do {
        const response = await api.get<PaginatedResponse<Product>>(`/products?per_page=100&page=${page}`);
        allProducts.push(...response.data);
        lastPage = response.last_page;
        page++;
      } while (page <= lastPage);

      return allProducts;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  /**
   * Get paginated products
   * @param page - Page number
   * @param perPage - Items per page
   * @returns Paginated response with products
   */
  async getPaginated(page: number = 1, perPage: number = 12): Promise<PaginatedResponse<Product>> {
    try {
      return await api.get<PaginatedResponse<Product>>(`/products?page=${page}&per_page=${perPage}`);
    } catch (error) {
      console.error('Error fetching paginated products:', error);
      throw error;
    }
  },

  /**
   * Get product by ID
   * @param id - Product ID
   * @returns Product data
   */
  async getById(id: number): Promise<Product> {
    try {
      return await api.get<Product>(`/products/${id}`);
    } catch (error) {
      console.error(`Error fetching product ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get products by category
   * @param category - Category name
   * @returns List of products
   */
  async getByCategory(category: string): Promise<Product[]> {
    try {
      return await api.get<Product[]>(`/products/category/${category}`);
    } catch (error) {
      console.error(`Error fetching products for category ${category}:`, error);
      throw error;
    }
  },

  /**
   * Search products
   * @param query - Search query
   * @returns List of products
   */
  async search(query: string): Promise<Product[]> {
    try {
      return await api.get<Product[]>(`/products/search?q=${encodeURIComponent(query)}`);
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  },

  /**
   * Create new product (Admin only)
   * @param productData - Product data
   * @param imageFiles - Array of image files to upload
   * @param bannerFile - Banner file to upload
   * @returns Created product
   */
  async create(productData: Record<string, any>, imageFiles: File[] = [], bannerFile: File | null = null): Promise<Product> {
    try {
      const formData = new FormData();

      // Append text fields
      Object.keys(productData).forEach(key => {
        if (key === 'image_urls') {
          // Handle image URLs array
          if (Array.isArray(productData[key])) {
            productData[key].forEach((url: string, index: number) => {
              if (url) formData.append(`image_urls[${index}]`, url);
            });
          }
        } else if (key === 'variants') {
          formData.append('variants', JSON.stringify(productData[key]));
        } else if (productData[key] !== null && productData[key] !== undefined) {
          // Convert boolean to number (0 or 1) for Laravel
          const value = typeof productData[key] === 'boolean'
            ? (productData[key] ? '1' : '0')
            : productData[key];
          formData.append(key, value);
        }
      });

      // Append image files
      imageFiles.forEach((file, index) => {
        formData.append(`images[${index}]`, file);
      });

      // Append banner file
      if (bannerFile) {
        formData.append('banner', bannerFile);
      }

      return await api.postFormData<Product>('/admin/products', formData);
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  /**
   * Update product (Admin only)
   * @param id - Product ID
   * @param productData - Updated product data
   * @param imageFiles - Array of image files to upload
   * @param bannerFile - Banner file to upload
   * @returns Updated product
   */
  async update(id: number, productData: Record<string, any>, imageFiles: File[] = [], bannerFile: File | null = null): Promise<Product> {
    try {
      // If no files, use regular JSON PUT request
      if (imageFiles.length === 0 && !bannerFile) {
        // Convert existing_images and deleted_image_urls arrays to JSON strings
        const dataToSend = { ...productData };
        if (dataToSend.existing_images && Array.isArray(dataToSend.existing_images)) {
          dataToSend.existing_images = JSON.stringify(dataToSend.existing_images);
        }
        if (dataToSend.deleted_image_urls && Array.isArray(dataToSend.deleted_image_urls)) {
          dataToSend.deleted_image_urls = JSON.stringify(dataToSend.deleted_image_urls);
        }
        return await api.put<Product>(`/admin/products/${id}`, dataToSend);
      }

      // If files exist, use FormData with POST (Laravel method spoofing)
      const formData = new FormData();
      formData.append('_method', 'PUT'); // Laravel method spoofing for FormData

      // Append text fields
      Object.keys(productData).forEach(key => {
        if (key === 'existing_images') {
          // Handle existing images array
          if (Array.isArray(productData[key])) {
            formData.append('existing_images', JSON.stringify(productData[key]));
          }
        } else if (key === 'deleted_image_urls') {
          // Handle deleted image URLs array
          if (Array.isArray(productData[key])) {
            formData.append('deleted_image_urls', JSON.stringify(productData[key]));
          }
        } else if (key === 'variants') {
          // Serialize variants to JSON string
          formData.append('variants', JSON.stringify(productData[key]));
        } else if (key === 'image_urls') {
          // Handle image URLs array
          if (Array.isArray(productData[key])) {
            productData[key].forEach((url: string, index: number) => {
              if (url) formData.append(`image_urls[${index}]`, url);
            });
          }
        } else if (productData[key] !== null && productData[key] !== undefined) {
          // Convert boolean to number (0 or 1) for Laravel
          const value = typeof productData[key] === 'boolean'
            ? (productData[key] ? '1' : '0')
            : productData[key];
          formData.append(key, value);
        }
      });

      // Append image files
      imageFiles.forEach((file, index) => {
        formData.append(`images[${index}]`, file);
      });

      // Append banner file
      if (bannerFile) {
        formData.append('banner', bannerFile);
      }

      return await api.postFormData<Product>(`/admin/products/${id}`, formData);
    } catch (error) {
      console.error(`Error updating product ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete product (Admin only)
   * @param id - Product ID
   */
  async delete(id: number): Promise<void> {
    try {
      return await api.delete<void>(`/admin/products/${id}`);
    } catch (error) {
      console.error(`Error deleting product ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get products with banners enabled
   * @returns List of products with banners
   */
  async getBanners(): Promise<Product[]> {
    try {
      return await api.get<Product[]>('/products/banners/active');
    } catch (error) {
      console.error('Error fetching product banners:', error);
      throw error;
    }
  },
};
